import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { settingsApi } from '../api';

/**
 * The Settings → General values in force for the signed-in user's organization.
 *
 * Read once when the app opens (and again after an administrator saves), so the
 * chat's follow-up suggestions, the sidebar's list size and the password hint follow
 * what was configured. The defaults below are the API's own built-in values: they are
 * what renders before the first answer arrives, if the request fails, and in tests
 * that mount a component without the provider.
 */

export const RUNTIME_DEFAULTS = Object.freeze({
  follow_up_suggestions: [
    'Why did you choose that value?',
    'Which attributes are you least certain about?',
    'What would raise this segment’s star rating?',
    'Re-check the pedestrian crossing attributes.',
  ],
  conversation_list_size: 60,
  min_password_length: 10,
});

const RuntimeSettingsContext = createContext({
  effective: RUNTIME_DEFAULTS,
  loaded: false,
  refresh: async () => null,
  apply: () => {},
});

export function RuntimeSettingsProvider({ children }) {
  const [effective, setEffective] = useState(RUNTIME_DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const apply = useCallback((view) => {
    if (view?.effective) setEffective({ ...RUNTIME_DEFAULTS, ...view.effective });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const view = await settingsApi.runtime();
      apply(view);
      return view;
    } catch {
      // Not worth a toast: every value here has a sensible default, and the page
      // that edits these settings reports its own errors.
      return null;
    } finally {
      setLoaded(true);
    }
  }, [apply]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ effective, loaded, refresh, apply }),
    [effective, loaded, refresh, apply],
  );
  return (
    <RuntimeSettingsContext.Provider value={value}>{children}</RuntimeSettingsContext.Provider>
  );
}

export function useRuntimeSettings() {
  return useContext(RuntimeSettingsContext);
}
